import Image from 'next/image';
import useTranslation from 'next-translate/useTranslation';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import classNames from 'classnames';
import { getApp } from 'firebase/app';
import classes from './PlacesAutocomplete.module.css';
import ClearIcon from 'static/icons/clear.png';
import { Place } from 'utils/interfaces/announcementForm';
import {
   countryPlaceIdToCode,
   getLocaleForGoogleApi,
   getPlaceFromMapLocalization,
} from 'utils/functions/placeFunctions';
import { GooglePlace } from 'utils/interfaces/place';
import { useRouter } from 'next/router';

type PlacesAutocompleteProps = {
   value: Place | null;
   onChange: (place: Place) => void;
   onClear?: () => void;
   placeholder?: string;
   label?: string;
   onlyCities?: boolean;
   required?: boolean;
   error?: boolean;
   helperText?: string;
   lockToCountry?: string;
};

const PlacesAutocomplete = ({
   value,
   onChange,
   onClear,
   placeholder,
   label,
   onlyCities,
   required,
   error,
   helperText,
   lockToCountry,
}: PlacesAutocompleteProps) => {
   const { t } = useTranslation('common');
   const placeValue = value?.city || value?.country || '';
   const router = useRouter();

   const locale = getLocaleForGoogleApi(router.locale);

   return (
      <div className={classNames(classes.placesAutocompleteContainer, { [classes.error]: error })}>
         <span className={classes.label}>
            {label} {required && <span> *</span>}
         </span>
         <div className={classes.innerContainer}>
            {locale && (
               <GooglePlacesAutocomplete
                  apiOptions={{ language: locale }}
                  selectProps={{
                     id: 'googlePlacesAutocomplete',
                     instanceId: 'googlePlacesAutocomplete',
                     value: placeValue,
                     onChange: (place: GooglePlace) => {
                        onChange(getPlaceFromMapLocalization(place));
                     },
                     placeholder: placeValue || placeholder || t('select_place_placeholder'),
                     className: classes.autoComplete,
                     noOptionsMessage: () => t('no_results'),
                     loadingMessage: () => t('loading'),
                  }}
                  autocompletionRequest={{
                     types: onlyCities ? ['(cities)'] : ['(regions)'],
                     ...(lockToCountry && {
                           componentRestrictions: {
                              country: [countryPlaceIdToCode(lockToCountry)],
                           },
                        }),
                  }}
                  apiKey={getApp().options.apiKey}
                  debounce={800}
               />
            )}
            {onClear && value && (
               <button onClick={onClear} className={classes.autoCompleteClearButton}>
                  <Image src={ClearIcon} alt={t('clear')} width={24} height={24} />
               </button>
            )}
         </div>
         {error && <p>{t(helperText!)}</p>}
      </div>
   );
};
export default PlacesAutocomplete;
